/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ViewEncapsulation,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RoutingService } from 'src/app/service/routing.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AuthService } from 'src/app/service/auth.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { UserSettingsApiService } from 'src/app/shared/services/customer-api/user-settings-api.service';
import { YukkApi } from 'src/app/service/yukkapi.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  finalize,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs/operators';
import { ColorPipe } from 'src/app/pipe/color.pipe';
import { ColorTrendingPipe } from 'src/app/pipe/colorTrending.pipe';
import { ColorTrendingReversedPipe } from 'src/app/pipe/colorTrendingReversed.pipe';
import { ColorScorePipe } from 'src/app/pipe/color-score.pipe';
import { ColorScore2Pipe } from 'src/app/pipe/color-score2.pipe';
import { PortfolioIndexComponent } from '../../main-news/news-portfolio/portfolio-index/portfolio-index.component';
import { DialogTableComponent } from '../../../micro-components/dialog-table/dialog-table.component';
import { UntypedFormControl } from '@angular/forms';
import { ConfigService } from 'src/app/service/config.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { PortfolioImportComponent } from '../../main-news/news-portfolio/portfolio-import/portfolio-import.component';
import { PortfolioShareComponent } from '../../main-news/news-portfolio/portfolio-share/portfolio-share.component';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { PortfolioService } from 'src/app/shared/services/data-api/portfolio.service';
import { BreakpointService } from 'src/app/shared/services/breakpoints.service';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import {
  SCANNER_ITEMS_REFRESH_PARAMS,
  SCANNER_ITEMS_COLUMN_REFRESH_PARAMS,
  SCANNER_ITEMS_FLAG_REFRESH_PARAMS,
} from '../scanner.constants';
import { resolveSortOrder } from 'src/app/shared/constants/sort-param-map';

const SCANNER_SORT_MAP: Record<string, string> = {
  score: '-score',
  '-score': 'score',
  score_delta: '-score_delta',
  '-score_delta': 'score_delta',
  senti: '-sentiment',
  '-senti': 'sentiment',
  abc: 'entity.name',
  '-abc': '-entity.name',
  trend: '-sentiment_delta',
  '-trend': 'sentiment_delta',
  count: '-count',
  '-count': 'count',
  ratio: '-volume_ratio',
  '-ratio': 'volume_ratio',
  price_delta: '-price_delta',
  '-price_delta': 'price_delta',
  volatility: '-volatility',
  '-volatility': 'volatility',
};

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-scanner-items',
  templateUrl: './scanner-items.component.html',
  styleUrls: ['./scanner-items.component.scss'],
})
export class ScannerItemsComponent implements OnDestroy {
  @Output() itemsCountToParent = new EventEmitter<any>();
  protected ngUnsubscribe: Subject<void> = new Subject<void>();

  public Math = Math;

  /**
   * query parameters url
   */
  params: any;

  /**
   * scanner items based on portfolio
   */
  items: any;
  itemsCountMin: any;
  itemsCountMax: any;
  itemsTrendMax: any;
  itemsTrendMin: any;
  itemsRatioMax: any;
  itemsRatioMin: any;
  itemsScoreMin: any;
  itemsScoreMax: any;
  itemsScoreDeltaMin: any;
  itemsScoreDeltaMax: any;
  itemsScoresArrayMin = {};
  itemsScoresArrayMax = {};
  itemsScoresArrayDeltaMin = {};
  itemsScoresArrayDeltaMax = {};
  itemsLoaded: boolean;

  /**
   * id of the entity under mouse cursor
   */
  currentEntity: any;

  /**
   * sort order
   */
  myOrder: string;

  /**
   * true while the primary score request is in-flight;
   * rows are sorted alphabetically until scores arrive
   */
  sortingScore = false;

  /**
   * refresh by parameters
   */
  previousValue: any;

  /**
   * errors returned by backend
   */
  nodata: boolean;

  /**
   * loading backend response
   */
  loading: boolean;

  /**
   * columns to display
   */
  ifilter: any;

  /**
   * columns array
   */
  array_filter = [];

  /**
   * color theme
   */
  theme = 'dark';

  /**
   * tracks which score columns are currently loading
   */
  loadingColumns: Set<string> = new Set();

  screenSize$ = this.breakpointService.screenSize$;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    public routing: RoutingService,
    public auth: AuthService,
    private yukkApi: YukkApi,
    private portfolioService: PortfolioService,
    public config: ConfigService,
    public snackBar: MatSnackBar,
    private breakpointService: BreakpointService,
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
    public portfolioApi: PortfolioApiService,
    private userSettingsApi: UserSettingsApiService,
  ) {
    this.route.queryParams.subscribe((params) => {
      this.params = JSON.parse(JSON.stringify(params));

      this.theme = this.routing.theme;

      if (params.sort) {
        const mapped = resolveSortOrder(params.sort, SCANNER_SORT_MAP, null);
        if (mapped) {
          this.myOrder = mapped;
        } else {
          this.myOrder =
            params.sort.charAt(0) === '-'
              ? params.sort.substring(1)
              : '-' + params.sort;
        }
      } else {
        if (this.auth.scorelabSettings.defaultScore !== 'sentiment') {
          this.myOrder = 'score';
          this.params.sort = 'score';
        } else {
          this.myOrder = 'sentiment';
          this.params.sort = '-senti';
        }
      }
      // Shared lazy fetch of scanner column settings — both the column-filter
      // UI setup and `loadAdditionalScoresIfNeeded` need it. shareReplay
      // ensures one HTTP per queryParams emission even when both subscribe.
      const scannerColumns$ = this.userSettingsApi
        .getUserSettings('scannerColumns')
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));

      if (
        this.params.id &&
        this.routing.reFresh(
          this.params,
          this.previousValue,
          SCANNER_ITEMS_COLUMN_REFRESH_PARAMS as unknown as string[],
        )
      ) {
        this.array_filter = [];
        this.ifilter = new UntypedFormControl([]);
        this.breakpointService.screenSize$
          .pipe(take(1))
          .subscribe((screen: { isMobile: boolean }) => {
            if (screen.isMobile) {
              this.array_filter = [this.auth.scorelabSettings.defaultScore];
              this.ifilter = new UntypedFormControl([
                this.auth.scorelabSettings.defaultScore,
              ]);
            } else {
              this.auth.getScoresInfo()['yukka'].forEach((score) => {
                this.array_filter.push(score.id);
                this.array_filter.push(score.id + '_delta');
              });
              this.auth.getScoresInfo()['custom'].forEach((score) => {
                this.array_filter.push(score.id);
                this.array_filter.push(score.id + '_delta');
              });
              this.array_filter.push('volume');
              this.array_filter.push('volume_ratio');
              this.array_filter.push('price');
              this.array_filter.push('price_delta');
              this.array_filter.push('volatility');
              scannerColumns$.subscribe((result) => {
                if (result.entry) {
                  let resultObj = JSON.parse(result.entry);
                  resultObj = resultObj.filter((item) => {
                    return this.array_filter.includes(item);
                  });
                  this.ifilter = new UntypedFormControl([
                    ...new Set([
                      ...[
                        this.auth.scorelabSettings.defaultScore,
                        this.auth.scorelabSettings.defaultScore + '_delta',
                      ],
                      ...resultObj,
                    ]),
                  ]);
                } else {
                  this.ifilter = new UntypedFormControl([
                    this.auth.scorelabSettings.defaultScore,
                    this.auth.scorelabSettings.defaultScore + '_delta',
                    'volume',
                    'volume_ratio',
                  ]);
                }
              });
            }
          });
      }

      if (
        this.params.id &&
        this.routing.reFresh(
          this.params,
          this.previousValue,
          SCANNER_ITEMS_REFRESH_PARAMS as unknown as string[],
        )
      ) {
        // abort unwanted HTTP requests
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        this.ngUnsubscribe = new Subject();

        this.nodata = false;
        this.loading = true;
        this.sortingScore = false;
        this.items = null;
        this.itemsLoaded = false;
        this.loadingColumns.clear();
        const selectedPortfolio = this.portfolioApi.activePortfolio
          ? this.portfolioApi.activePortfolio.content
          : [];
        this.portfolioService
          .getPortfolio(
            Object.assign({}, this.params, { getSanctions: true }),
            selectedPortfolio,
          )
          .subscribe(
            (result) => {
              const content = [];
              this.items = result.treemap.children;
              this.loading = false;
              this.sortingScore = true;
              this.items.forEach((item) => {
                content.push(item.entity.compound_key);
                item.entity.name =
                  item.entity.name !== 'PNE'
                    ? item.entity.name
                    : 'PNE: ' +
                      item.entity.alpha_id.split('.')[1] +
                      ' (' +
                      item.entity.alpha_id.split('.')[0] +
                      ')';
                // Sentiment must come from score_ts v2; drop the legacy
                delete item.sentiment;
                delete item.sentiment_delta;
              });
              this.updateStats();
              const itemsLength = this.items.length;
              this.itemsCountToParent.emit(itemsLength);
              if (itemsLength > 0) {
                this.fetchSentimentV2(content);
                if (this.auth.scorelabSettings.defaultScore !== 'sentiment') {
                  this.fetchDefaultScoreV2(content);
                }
                this.loadAdditionalScoresIfNeeded(scannerColumns$);
              } else {
                this.sortingScore = false;
              }
            },
            () => {
              this.nodata = true;
              this.loading = false;
            },
          );
      }
      if (
        this.params.id &&
        this.routing.reFresh(
          this.params,
          this.previousValue,
          SCANNER_ITEMS_FLAG_REFRESH_PARAMS as unknown as string[],
        )
      ) {
        if (this.auth.scorelabSettings.defaultFlag && this.items) {
          if (this.auth.scorelabSettings.defaultFlag.score_type === 'volume') {
            this.getCurrentCount();
          } else {
            // abort unwanted HTTP requests
            this.ngUnsubscribe.next();
            this.ngUnsubscribe.complete();
            this.ngUnsubscribe = new Subject();
            this.getScore(this.auth.scorelabSettings.defaultFlag.score_type);
          }
        }
      }

      this.previousValue = params;
    });
  }

  ngOnDestroy() {
    // abort unwanted HTTP requests
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * get news count for current day
   */
  getCurrentCount() {
    const itemsLength = this.items.length;
    if (itemsLength > 0) {
      const content = this.portfolioApi.activePortfolio
        ? this.portfolioApi.activePortfolio.content
        : [];
      this.portfolioService
        .getPortfolio(
          Object.assign({}, this.params, {
            time: 1,
          }),
          content,
        )
        .subscribe((result) => {
          result.treemap.children.forEach((item: any, index) => {
            this.items[index]['currentCount'] = item.count;
          });
        });
    }
  }

  /**
   * get score and change in score for a particular score id
   */
  getScore(scoreId) {
    const itemsLength = this.items.length;
    const mainId = this.getScoreId(scoreId).scoreId;

    // Skip if already fetched, already loading, no items, is sentiment/volume, or is default score
    if (
      itemsLength === 0 ||
      this.loadingColumns.has(mainId) ||
      Object.prototype.hasOwnProperty.call(this.items[0], scoreId) ||
      ['sentiment', 'sentiment_delta', 'volume', 'volume_ratio'].includes(
        scoreId,
      ) ||
      mainId === this.auth.scorelabSettings.defaultScore
    ) {
      return;
    }

    // Mark column as loading
    this.loadingColumns.add(mainId);

    // Keep legacy API for price, volatility, and industry benchmarks
    if (
      ['price', 'price_delta', 'volatility'].includes(mainId) ||
      mainId.includes('industry_bench')
    ) {
      this.getScoreLegacy(scoreId);
      return;
    }

    // Use v2 API for all other scores
    const content = this.portfolioApi.activePortfolio
      ? this.portfolioApi.activePortfolio.content
      : [];
    const resolvedScore = this.scoreUtils.resolveScoreType(mainId);

    this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {},
        body: {
          score_type: resolvedScore,
          entity: content,
          time: this.params.time || '7',
        },
      })
      .pipe(
        catchError(() => of({ score_ts: [] })),
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.loadingColumns.delete(mainId)),
      )
      .subscribe((response: any) => {
        const allScoreResults = response.score_ts || [];
        this.applyAdditionalScoresToItems(allScoreResults, mainId);
        this.calculateAdditionalScoreStats(mainId);
      });
  }

  /**
   * Legacy getScore implementation for price, volatility, and industry benchmarks
   */
  private getScoreLegacy(scoreId) {
    const mainId = this.getScoreId(scoreId).scoreId;
    const content = this.portfolioApi.activePortfolio
      ? this.portfolioApi.activePortfolio.content
      : [];
    const params = JSON.parse(JSON.stringify(this.params));
    if (!params.time) {
      params['time'] = '7';
    }
    this.yukkApi
      .scoresTimeSeries(
        !['price', 'price_delta', 'volatility'].includes(scoreId)
          ? 'score'
          : scoreId,
        Object.assign({}, params, {
          scoreForPortfolio: true,
          requestScore: mainId.includes('industry_bench')
            ? this.auth.scorelabSettings.defaultScore
            : mainId,
          industry_bench: mainId.includes('industry_bench') ? true : false,
          itemized: true,
          custom_payload: content,
        }),
        'chart',
      )
      .pipe(
        catchError(() => of(undefined)),
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.loadingColumns.delete(mainId)),
      )
      .subscribe((entityScores: any) => {
        if (!this.items || !entityScores) {
          return;
        }
        entityScores.forEach((entityScore, index) => {
          if (!this.items[index]) {
            return;
          }
          if (['price', 'price_delta', 'volatility'].includes(mainId)) {
            this.items[index][mainId] =
              entityScore &&
              entityScore.values &&
              entityScore.values[entityScore.values.length - 1].value !== null
                ? mainId === 'price'
                  ? Math.round(
                      entityScore.values[entityScore.values.length - 1].value,
                    )
                  : entityScore.values[entityScore.values.length - 1].value
                : null;
            if (mainId === 'price' && entityScore.currency) {
              this.items[index]['currency'] = entityScore.currency;
            }
          } else {
            const lastScore =
              entityScore && entityScore.score_ts
                ? ScoreUtilsService.getLastScore(entityScore.score_ts)
                : null;
            this.items[index][mainId] = lastScore;

            if (!mainId.includes('industry_bench')) {
              this.items[index][mainId + '_delta'] =
                lastScore !== null
                  ? lastScore - entityScore.score_ts[0].score
                  : null;
            } else {
              if (entityScore && entityScore.score_ts) {
                let industry = 'Industry';
                if (this.items[index].entity?.description?.Industry) {
                  industry = this.items[index].entity.description.Industry.name;
                }
                let description = '';
                if (
                  entityScore.score_ts[entityScore.score_ts.length - 1]
                    .score === 0.0
                ) {
                  description = 'Laggard';
                } else if (
                  entityScore.score_ts[entityScore.score_ts.length - 1]
                    .score === 1.0
                ) {
                  description = 'Below average';
                } else if (
                  entityScore.score_ts[entityScore.score_ts.length - 1]
                    .score === 2.0
                ) {
                  description = 'Above average';
                } else if (
                  entityScore.score_ts[entityScore.score_ts.length - 1]
                    .score === 3.0
                ) {
                  description = 'Leader';
                }
                this.items[index][mainId + '_info'] =
                  description + ' in ' + industry;
              } else {
                this.items[index][mainId + '_info'] = '';
              }
            }

            if (['bb'].includes(mainId)) {
              const trend = ScoreUtilsService.getBullBearTrend(lastScore);
              this.items[index][mainId + '_trend_type'] = trend.trendType;
              this.items[index][mainId + '_trend_value'] = trend.trendValue;
            }
          }
        });
        if (!mainId.includes('industry_bench')) {
          const itemsWithScore = this.items.filter((item) => {
            return item[mainId];
          });
          this.itemsScoresArrayMax[mainId] = Math.max(
            ...itemsWithScore.map((item) => {
              return item[mainId];
            }),
            1,
          );
          this.itemsScoresArrayMin[mainId] = Math.min(
            ...itemsWithScore.map((item) => {
              return item[mainId];
            }),
            0,
          );
          const itemsWithScoreDelta = this.items.filter((item) => {
            return item[mainId + '_delta'];
          });
          this.itemsScoresArrayDeltaMax[mainId] = Math.max(
            ...itemsWithScoreDelta.map((item) => {
              return item[mainId + '_delta'];
            }),
            1,
          );
          this.itemsScoresArrayDeltaMin[mainId] = Math.min(
            ...itemsWithScoreDelta.map((item) => {
              return item[mainId + '_delta'];
            }),
            0,
          );
        }
      });
  }

  /**
   * Apply additional score results to items (for non-default scores)
   */
  private applyAdditionalScoresToItems(
    scoreResults: any[],
    scoreId: string,
  ): void {
    const scoreMap = new Map<string, any>();
    scoreResults.forEach((result) => {
      if (result.entity_info?.compound_key) {
        scoreMap.set(result.entity_info.compound_key, result);
      }
    });

    this.items.forEach((item) => {
      const compoundKey = item.entity.compound_key;
      const scoreResult = scoreMap.get(compoundKey);

      if (scoreResult?.score_ts?.length > 0) {
        const scoreTsArray = scoreResult.score_ts;
        const lastScore = ScoreUtilsService.getLastScore(scoreTsArray);
        const firstScore = scoreTsArray[0].score;

        item[scoreId] = lastScore;
        item[scoreId + '_delta'] =
          lastScore !== null ? lastScore - firstScore : null;

        // Handle bull_bear trend_type/trend_value for additional bb columns
        if (['bb'].includes(scoreId)) {
          const trend = ScoreUtilsService.getBullBearTrend(lastScore);
          item[scoreId + '_trend_type'] = trend.trendType;
          item[scoreId + '_trend_value'] = trend.trendValue;
        }
      } else {
        item[scoreId] = null;
        item[scoreId + '_delta'] = null;
      }
    });
  }

  /**
   * Calculate min/max stats for additional score columns
   */
  private calculateAdditionalScoreStats(scoreId: string): void {
    const definedItems = this.items.filter((item) => item[scoreId] !== null);
    this.itemsScoresArrayMax[scoreId] = Math.max(
      ...definedItems.map((item) => item[scoreId]),
      1,
    );
    this.itemsScoresArrayMin[scoreId] = Math.min(
      ...definedItems.map((item) => item[scoreId]),
      0,
    );

    const definedItems2 = this.items.filter(
      (item) => item[scoreId + '_delta'] !== null,
    );
    this.itemsScoresArrayDeltaMax[scoreId] = Math.max(
      ...definedItems2.map((item) => item[scoreId + '_delta']),
      1,
    );
    this.itemsScoresArrayDeltaMin[scoreId] = Math.min(
      ...definedItems2.map((item) => item[scoreId + '_delta']),
      0,
    );
  }

  /**
   * check if item should be flagged
   */
  isFlagged(item) {
    if (
      item &&
      item.entity &&
      this.auth.scorelabSettings.defaultFlag &&
      this.auth.scorelabSettings.defaultFlag.entity_type.includes(
        item.entity.type,
      )
    ) {
      let currentScore;
      if (this.auth.scorelabSettings.defaultFlag.score_type === 'volume') {
        // currentScore = item.count;
        currentScore = item.currentCount;
      } else if (
        this.auth.scorelabSettings.defaultFlag.score_type === 'sentiment'
      ) {
        currentScore = item.sentiment * 100;
      } else if (this.auth.scorelabSettings.defaultFlag.score_type) {
        if (
          this.auth.scorelabSettings.defaultFlag.score_type ===
          this.auth.scorelabSettings.defaultScore
        ) {
          currentScore = item.score;
        } else if (
          Object.prototype.hasOwnProperty.call(
            item,
            this.auth.scorelabSettings.defaultFlag.score_type,
          )
        ) {
          currentScore =
            item[this.auth.scorelabSettings.defaultFlag.score_type];
        } else {
          return false;
        }
      } else {
        return false;
      }
      if (currentScore !== null && currentScore !== undefined) {
        if (
          this.auth.scorelabSettings.defaultFlag.threshold_setting ===
          'ABOVE_EQUAL'
        ) {
          if (
            currentScore >= this.auth.scorelabSettings.defaultFlag.threshold
          ) {
            return currentScore;
          } else {
            return false;
          }
        } else if (
          this.auth.scorelabSettings.defaultFlag.threshold_setting ===
          'BELOW_EQUAL'
        ) {
          if (
            currentScore <= this.auth.scorelabSettings.defaultFlag.threshold
          ) {
            return currentScore;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * check whether requests for assigning flags are still loading
   */
  loadingFlag(item) {
    if (
      item &&
      item.entity &&
      this.auth.scorelabSettings.defaultFlag &&
      this.auth.scorelabSettings.defaultFlag.entity_type.includes(
        item.entity.type,
      )
    ) {
      if (this.auth.scorelabSettings.defaultFlag.score_type === 'volume') {
        if (item.currentCount !== undefined) {
          return false;
        } else {
          return true;
        }
      } else if (
        this.auth.scorelabSettings.defaultFlag.score_type === 'sentiment'
      ) {
        if (item.sentiment !== undefined) {
          return false;
        } else {
          return true;
        }
      } else if (this.auth.scorelabSettings.defaultFlag.score_type) {
        if (
          this.auth.scorelabSettings.defaultFlag.score_type ===
          this.auth.scorelabSettings.defaultScore
        ) {
          if (item.score !== undefined) {
            return false;
          } else {
            return true;
          }
        } else if (
          item[this.auth.scorelabSettings.defaultFlag.score_type] !== undefined
        ) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
    // if (item && item.entity && this.auth.scorelabSettings.defaultFlag && this.auth.scorelabSettings.defaultFlag.entity_type.includes(item.entity.type)) {
    //   return true;
    // } else {
    //   return false;
    // }
  }

  /**
   * get tooltip for the particular item's flag
   */
  getFlagTooltip(item) {
    const threshold = this.auth.scorelabSettings.defaultFlag.threshold;
    const scoreName = this.auth.getScoresInfo(
      this.auth.scorelabSettings.defaultFlag.score_type,
    )['full'];
    let score;
    if (
      this.auth.scorelabSettings.defaultFlag.score_type === 'sentiment' ||
      this.auth.scorelabSettings.defaultFlag.score_type === 'volume'
    ) {
      score = this.isFlagged(item) + ' of the ' + scoreName;
    } else {
      score = Number.parseFloat(this.isFlagged(item)).toFixed(2);
    }
    let tooltip = '';
    if (
      this.auth.scorelabSettings.defaultFlag.score_type === 'sentiment' ||
      this.auth.scorelabSettings.defaultFlag.score_type === 'volume'
    ) {
      tooltip =
        'Current value ' +
        score +
        ' crossed the set threshold of ' +
        threshold +
        '.';
    } else {
      tooltip =
        'One or more of the events associated with ' +
        scoreName +
        ' occurred in the past 100 days. Current value ' +
        score +
        ' crossed the set threshold of ' +
        threshold +
        '.';
    }
    return tooltip;
  }

  /**
   * get score's details based on id
   */
  getScoreId(id) {
    const scoreIdArray = id.split('_');
    const isDelta = scoreIdArray.includes('delta') ? true : false;
    const scoreId = scoreIdArray.filter((item) => item !== 'delta').join('_');
    if (['price', 'price_delta', 'volatility'].includes(id)) {
      return { scoreId: id, isDelta: null };
    } else {
      return { scoreId, isDelta };
    }
  }

  /**
   * get color from color pipes based on score's type and value
   * @param item
   */
  getScoreColor(item) {
    // if (this.myOrder === 'entity.name' || this.myOrder === '-entity.name') {
    //   const filterPipe = new ColorPipe(this.routing);
    //   return filterPipe.transform(item.sentiment);
    // }
    if (this.myOrder === '-sentiment' || this.myOrder === 'sentiment') {
      const filterPipe = new ColorPipe(this.routing);
      return filterPipe.transform(item.sentiment);
    } else if (
      this.myOrder === '-sentiment_delta' ||
      this.myOrder === 'sentiment_delta'
    ) {
      const filterPipe = new ColorTrendingPipe(this.routing);
      return filterPipe.transform(
        item.sentiment_delta,
        this.itemsTrendMin,
        this.itemsTrendMax,
      );
    } else if (this.myOrder === '-count' || this.myOrder === 'count') {
      const filterPipe = new ColorPipe(this.routing);
      return filterPipe.transform(item.count / this.itemsCountMax);
    } else if (
      this.myOrder === '-volume_ratio' ||
      this.myOrder === 'volume_ratio'
    ) {
      const filterPipe = new ColorTrendingPipe(this.routing);
      let value = item.volume_ratio
        ? item.volume_ratio * 100.0 - 100.0
        : undefined;
      if (
        (item.volume_ratio === null || item.volume_ratio === 0) &&
        item.count &&
        item.count > 0
      ) {
        value = this.itemsRatioMin * 100.0 - 100.0;
      }
      return filterPipe.transform(
        value,
        this.itemsRatioMin * 100.0 - 100.0,
        this.itemsRatioMax * 100.0 - 100.0,
      );
    } else if (
      this.myOrder === 'score_delta' ||
      this.myOrder === '-score_delta'
    ) {
      const filterPipe =
        this.auth.getScoreType() === 'pos'
          ? new ColorTrendingPipe(this.routing)
          : new ColorTrendingReversedPipe(this.routing);
      return filterPipe.transform(
        item.score_delta,
        this.itemsScoreDeltaMin,
        this.itemsScoreDeltaMax,
      );
    } else if (this.myOrder === 'score' || this.myOrder === '-score') {
      const filterPipe =
        this.auth.getScoreType() === 'pos'
          ? new ColorScore2Pipe(this.routing)
          : new ColorScorePipe(this.routing);
      return filterPipe.transform(item.score);
    } else {
      let scoreId = '';
      if (this.myOrder.charAt(0) === '-') {
        scoreId = this.myOrder.substring(1);
      } else {
        scoreId = this.myOrder;
      }
      const scoreMain = this.getScoreId(scoreId);
      if (scoreMain.isDelta) {
        const filterPipe =
          this.auth.getScoreType(scoreMain.scoreId) === 'pos'
            ? new ColorTrendingPipe(this.routing)
            : new ColorTrendingReversedPipe(this.routing);
        return filterPipe.transform(
          item[scoreMain.scoreId + '_delta'],
          this.itemsScoresArrayDeltaMin[scoreMain.scoreId],
          this.itemsScoresArrayDeltaMax[scoreMain.scoreId],
        );
      } else {
        const filterPipe =
          this.auth.getScoreType(scoreMain.scoreId) === 'pos'
            ? new ColorScore2Pipe(this.routing)
            : new ColorScorePipe(this.routing);
        return filterPipe.transform(item[scoreMain.scoreId]);
      }
    }
  }

  /**
   * get name of the filter based on its id
   */
  getFilterName(filter) {
    if (filter === 'volume') {
      return 'Volume';
    } else if (filter === 'volume_ratio') {
      return 'Volume change';
    } else if (filter === 'price') {
      return 'Price';
    } else if (filter === 'price_delta') {
      return 'Price change';
    } else if (filter === 'volatility') {
      return 'Volatility';
    } else if (this.getScoreId(filter).isDelta) {
      return (
        this.auth.getScoresInfo(this.getScoreId(filter).scoreId)['name'] +
        ' change'
      );
    } else {
      return this.auth.getScoresInfo(this.getScoreId(filter).scoreId)['name'];
    }
  }

  /**
   * apply chosen sorting option
   */
  goSort(value) {
    if (this.params.sort === value) {
      this.router.navigate([], {
        queryParams: { sort: '-' + value },
        queryParamsHandling: 'merge',
        replaceUrl: false,
      });
    } else {
      this.router.navigate([], {
        queryParams: { sort: value },
        queryParamsHandling: 'merge',
        replaceUrl: false,
      });
    }
  }

  /**
   * add tag to the url on item click
   */
  addTag(item) {
    this.breakpointService.screenSize$
      .pipe(take(1))
      .subscribe((screen: { isMobile: boolean }) => {
        if (!screen.isMobile) {
          const tagItem = 'm:' + item.entity.type + ':' + item.entity.alpha_id;
          const tag = this.params.tag === tagItem ? null : encodeURI(tagItem);
          this.router.navigate([], {
            queryParams: {
              tag: tag,
              eventid: null,
              eventype: null,
              scoreevent: null,
              flagevent: null,
              eventsFromScanner: null,
              newstype:
                this.params.newstype === 'Events'
                  ? 'Chronological'
                  : this.params.newstype,
            },
            queryParamsHandling: 'merge',
            replaceUrl: false,
          });
        } else {
          this.router.navigate(['/cockpit/news/market/chart'], {
            queryParams: {
              type: item.entity.type,
              id: item.entity.alpha_id,
              tag: null,
              news: null,
              eventid: null,
              eventype: null,
              scoreevent: null,
              flagevent: null,
              newstype: null,
              eventsFromScanner: null,
            },
            queryParamsHandling: 'merge',
          });
        }
      });
  }

  /**
   * navigate to entity's page on click
   */
  navEntity(entity) {
    this.breakpointService.screenSize$
      .pipe(take(1))
      .subscribe((screen: { isMobile: boolean }) => {
        const path = screen.isMobile
          ? '/cockpit/news/market/chart'
          : '/cockpit/news/portfolio/chart';
        const focusedScore = screen.isMobile
          ? null
          : this.auth.scorelabSettings.defaultScore
            ? this.auth.scorelabSettings.defaultScore
            : null;
        const newstype =
          this.params.eventid && this.params.tag ? 'Events' : null;
        const portfolioId = screen.isMobile
          ? null
          : this.portfolioApi.activePortfolio
            ? this.portfolioApi.activePortfolio.uid
            : null;
        // check if the tag exist and match with the selected entitiy
        this.router.navigate([path], {
          queryParams: {
            type: entity.type,
            id: entity.alpha_id,
            portfolioId: portfolioId,
            tag: null,
            template: null,
            user: null,
            folio: null,
            folioupdate: null,
            period: null,
            news: null,
            eventid: this.params.eventid || null,
            eventype: this.params.eventype || null,
            newstype: newstype,
            eventsFromScanner: null,
            focused_score: focusedScore,
          },
          queryParamsHandling: 'merge',
        });
      });
  }

  onMouseEnterName(id) {
    this.currentEntity = id;
  }

  onMouseLeaveName() {
    this.currentEntity = null;
  }

  onMouseEnterRow(item) {
    item.visible = true;
  }

  onMouseLeaveRow(item) {
    item.visible = false;
  }

  /**
   * add new item
   */
  newIndex() {
    this.dialog.open(PortfolioIndexComponent, {
      data: {
        // uid: this.params.folio,
        action: 'new',
      },
    });
  }

  /**
   * edit specific item
   */
  editIndex(entity) {
    this.dialog.open(PortfolioIndexComponent, {
      data: {
        // uid: this.params.folio,
        action: 'edit',
        entity: entity,
      },
    });
  }

  /**
   * get columns to be displayed based on selected scores
   */
  getAvailableScores() {
    return this.ifilter.value.filter((item) => {
      return (
        ![
          'sentiment',
          'sentiment_delta',
          'volume',
          'volume_ratio',
          'price',
          'price_delta',
          'volatility',
        ].includes(item) &&
        !(item === this.auth.scorelabSettings.defaultScore) &&
        !(item === this.auth.scorelabSettings.defaultScore + '_delta')
      );
    });
  }

  /**
   * changed columns to be displayed
   */
  iFilter(event) {
    if (event === false) {
      const scannerColumns = this.ifilter.value.filter((item) => {
        return (
          item !== this.auth.scorelabSettings.defaultScore &&
          item !== this.auth.scorelabSettings.defaultScore + '_delta'
        );
      });
      const scannerColumnsValue = JSON.stringify(scannerColumns);
      this.userSettingsApi
        .setUserSettings('scannerColumns', scannerColumnsValue)
        .subscribe();
      scannerColumns.forEach((el) => {
        this.getScore(el);
      });
    }
  }

  /**
   * menage score filters selection, limit number of selected options to 6
   */
  isOptionDisabled(option) {
    return (
      (this.ifilter.value.length > 5 &&
        !this.ifilter.value.find((el) => el === option)) ||
      option === this.auth.scorelabSettings.defaultScore ||
      option === this.auth.scorelabSettings.defaultScore + '_delta'
    );
  }

  /**
   * check if column should be displayed
   */
  iTool(value) {
    return this.ifilter.value.includes(value);
  }

  /**
   * check if a score column is currently loading
   */
  isColumnLoading(scoreId: string): boolean {
    const mainId = this.getScoreId(scoreId).scoreId;
    return this.loadingColumns.has(mainId);
  }

  /**
   * open popup dialog with sanctions table
   */
  openDialog(entity) {
    this.dialog.open(DialogTableComponent, {
      data: {
        title: 'Sanctions on ' + entity.name,
        table: entity.sanctions.map((sanction) => ({
          authority: sanction.sanctioned_by.name,
          authorityId: sanction.sanctioned_by.alpha_id,
          authorityType: sanction.sanctioned_by.type,
          program: sanction.program,
          start: sanction.sanctioned_from,
          end: sanction.sanctioned_to,
        })),
      },
      width: '60%',
    });
  }

  /**
   * share portfolio with other users
   */
  iShare() {
    if (!this.portfolioApi.activePortfolio.sharing_mode) {
      this.dialog.open(PortfolioShareComponent, {
        data: {
          // uid: this.params.folio,
        },
      });
    }
  }

  /**
   * Import list of isins or portfolios from files
   */
  iImport(event, type) {
    if (event === null && type === 'isinsCreate') {
      this.dialog.open(PortfolioImportComponent, {
        data: {
          create: true,
          // uid: this.params.folio,
        },
      });
    } else if (event === null && type === 'isinsAdd') {
      this.dialog.open(PortfolioImportComponent, {
        data: {
          create: false,
          // uid: this.params.folio,
        },
      });
    } else if (event !== null && type === 'portfolio') {
      if (event.target.files && event.target.files[0]) {
        for (let i = 0; i < event.target.files.length; i++) {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            const result = JSON.parse(e.target.result);
            if (
              this.portfolioApi.activePortfolio &&
              this.portfolioApi.activePortfolio.content.length +
                result.content.length >
                this.auth.featureFlags.entitiesPerPortfoliosLimit
            ) {
              this.snackBar.open(
                'Number of entities per portfolio is limited to ' +
                  this.auth.featureFlags.entitiesPerPortfoliosLimit +
                  '.',
                'OK',
                { duration: 5000 },
              );
            } else {
              this.portfolioApi
                .addEntity(result.content)
                .subscribe((result2) => {
                  this.router.navigate([], {
                    queryParams: {
                      id: result2.id,
                      update: Math.random(),
                    },
                    queryParamsHandling: 'merge',
                  });
                });
            }
          };
          reader.readAsText(event.target.files[i]);
        }
      }
    }
  }

  /**
   * export as file xlsx
   */
  iExport(type) {
    if (type === 'portfolio') {
      if (this.portfolioApi.activePortfolio) {
        const portfolioPure = {
          name: this.portfolioApi.activePortfolio.name,
          content: this.portfolioApi.activePortfolio.content,
        };
        const portfolioString = JSON.stringify(portfolioPure);
        const blob = new Blob([portfolioString], { type: 'application/json' });
        FileSaver.saveAs(blob, 'portfolio_' + portfolioPure.name + '.json');
      }
    } else if (type === 'stats') {
      if (this.items && this.items.length > 0) {
        const date = new Date();
        const displayDate =
          date.getFullYear() +
          '-' +
          date.getMonth() +
          '-' +
          date.getDate() +
          ' ' +
          date.getHours() +
          '-' +
          date.getMinutes() +
          '-' +
          date.getSeconds();
        const doubleArrayData = [
          ['Name', 'Industry', 'CEO', 'Score', 'Sent', 'ΔSent', 'Vol', 'ΔVol'],
        ];
        this.items.forEach((company) => {
          doubleArrayData.push([
            company.entity && company.entity.name ? company.entity.name : '',
            company.entity &&
            company.entity.description &&
            company.entity.description.Industry &&
            company.entity.description.Industry.name
              ? company.entity.description.Industry.name
              : '',
            company.entity &&
            company.entity.description &&
            company.entity.description.CEO &&
            company.entity.description.CEO.name
              ? company.entity.description.CEO.name
              : '',
            company.score ? company.score : '',
            company.sentiment ? company.sentiment : '',
            company.sentiment_delta ? company.sentiment_delta : '',
            company.count ? company.count : '',
            company.volume_ratio ? company.volume_ratio : '',
          ]);
        });
        /* generate worksheet */
        const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(doubleArrayData);
        /* generate workbook and add the worksheet */
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        /* save to file */
        XLSX.writeFile(
          wb,
          'Portfolio statistics from ' + displayDate + '.xlsx',
        );
      }
    }
  }

  private updateStats() {
    const getMax = (key: string) =>
      Math.max(...this.items.map((item) => item[key]), 1);
    const getMin = (key: string) =>
      Math.min(...this.items.map((item) => item[key]), 0);

    this.itemsCountMax = getMax('count');
    this.itemsCountMin = getMin('count');
    this.itemsTrendMax = getMax('sentiment_delta');
    this.itemsTrendMin = getMin('sentiment_delta');
    this.itemsRatioMax = getMax('volume_ratio');
    this.itemsRatioMin = getMin('volume_ratio');
  }

  /**
   * Fetch the user's default score (anything other than sentiment) via v2.
   * Sentiment is always handled by fetchSentimentV2 because it owns its own
   * dedicated columns regardless of which score is the default.
   */
  private fetchDefaultScoreV2(content: string[]): void {
    const defaultScoreId = this.auth.scorelabSettings.defaultScore;
    const scoreType = this.scoreUtils.resolveScoreType(defaultScoreId);

    this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {}, // No collapse_on = itemized results
        body: {
          score_type: scoreType,
          entity: content,
          time: this.params.time || '7',
        },
      })
      .pipe(
        catchError(() => of({ score_ts: [] })),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((response: any) => {
        const allScoreResults = response.score_ts || [];
        this.applyScoresToItems(allScoreResults, 'score', 'score_delta', false);
        this.calculateScoreStats();
        this.itemsLoaded = true;
        this.sortingScore = false;
      });
  }

  /**
   * Fetch sentiment via score_ts v2. Always runs — sentiment has its own
   * dedicated columns (item.sentiment / item.sentiment_delta) that the UI
   * displays regardless of which score is the user's default. When sentiment
   * IS the default, this method also flips the sort-readiness flags.
   */
  private fetchSentimentV2(content: string[]): void {
    const isDefault = this.auth.scorelabSettings.defaultScore === 'sentiment';
    const timeframe = this.params.time || '7';
    const timeframeLength = DateUtils.chartFromTo(timeframe)?.period || 7;
    const period = timeframeLength <= 7 ? 7 : 30;

    this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {},
        body: {
          score_type: { period },
          entity: content,
          time: this.params.time || '7',
        },
      })
      .pipe(
        catchError(() => of({ score_ts: [] })),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((response: any) => {
        const allScoreResults = response.score_ts || [];
        this.applyScoresToItems(
          allScoreResults,
          'sentiment',
          'sentiment_delta',
          true,
        );
        if (isDefault) {
          this.itemsLoaded = true;
          this.sortingScore = false;
        }
      });
  }

  /**
   * Apply score results to items by matching compound_key
   */
  private applyScoresToItems(
    scoreResults: any[],
    scoreKey: string,
    deltaKey: string,
    isSentiment = false,
  ): void {
    // Create a map for O(1) lookup by compound_key
    const scoreMap = new Map<string, any>();
    scoreResults.forEach((result) => {
      if (result.entity_info?.compound_key) {
        scoreMap.set(result.entity_info.compound_key, result);
      }
    });

    // Apply scores to items by matching compound_key
    this.items.forEach((item) => {
      const compoundKey = item.entity.compound_key;
      const scoreResult = scoreMap.get(compoundKey);

      if (scoreResult?.score_ts?.length > 0) {
        const scoreTsArray = scoreResult.score_ts;
        const lastScore = ScoreUtilsService.getLastScore(scoreTsArray);
        const firstScore = scoreTsArray[0].score;

        item[scoreKey] = lastScore;
        item[deltaKey] = lastScore !== null ? lastScore - firstScore : null;

        // Handle bull_bear trend_type/trend_value
        if (
          ['bb'].includes(this.auth.scorelabSettings.defaultScore) &&
          !isSentiment
        ) {
          const trend = ScoreUtilsService.getBullBearTrend(lastScore);
          item['trend_type'] = trend.trendType;
          item['trend_value'] = trend.trendValue;
        }
      } else {
        item[scoreKey] = null;
        item[deltaKey] = null;
      }
    });
  }

  /**
   * Calculate score min/max stats for color scaling
   */
  private calculateScoreStats(): void {
    const definedItems = this.items.filter((item) => item.score !== null);
    this.itemsScoreMax = Math.max(...definedItems.map((item) => item.score), 1);
    this.itemsScoreMin = Math.min(...definedItems.map((item) => item.score), 0);

    const definedItems2 = this.items.filter(
      (item) => item.score_delta !== null,
    );
    this.itemsScoreDeltaMax = Math.max(
      ...definedItems2.map((item) => item.score_delta),
      1,
    );
    this.itemsScoreDeltaMin = Math.min(
      ...definedItems2.map((item) => item.score_delta),
      0,
    );
  }

  /**
   * Load additional scores based on user settings and flags.
   *
   * Accepts the shared `scannerColumns$` observable from the queryParams
   * handler so the user-settings HTTP isn't fetched a second time when the
   * column-filter UI block has already triggered it for this emission.
   */
  private loadAdditionalScoresIfNeeded(scannerColumns$: Observable<any>): void {
    // Load scores for saved scanner columns
    scannerColumns$.subscribe((res) => {
      if (res.entry) {
        let resultObj = JSON.parse(res.entry);
        resultObj = [
          ...new Set(
            resultObj.map((el) => {
              return this.getScoreId(el).scoreId;
            }),
          ),
        ];
        resultObj.forEach((el) => {
          this.getScore(el);
        });
        if (
          this.auth.scorelabSettings.defaultFlag &&
          !resultObj.includes(this.auth.scorelabSettings.defaultFlag.score_type)
        ) {
          this.getScore(this.auth.scorelabSettings.defaultFlag.score_type);
        }
      } else {
        if (this.auth.scorelabSettings.defaultFlag) {
          this.getScore(this.auth.scorelabSettings.defaultFlag.score_type);
        }
      }
    });

    // Load current count for volume flag
    if (
      this.auth.scorelabSettings.defaultFlag &&
      this.auth.scorelabSettings.defaultFlag.score_type === 'volume'
    ) {
      this.getCurrentCount();
    }

    // Load industry benchmark for supported score types
    if (
      [
        'esg',
        'eb_sim',
        'eb_sim_risk',
        'general_risk',
        'esg_risk',
        'immediate_risk',
        'credit_risk',
        'bb',
      ].includes(this.auth.scorelabSettings.defaultScore)
    ) {
      this.getScore(
        this.auth.scorelabSettings.defaultScore + '_industry_bench',
      );
    }
  }
}
